import axios from 'axios'
import Echo from '@ably/laravel-echo'
import * as Ably from 'ably'
window.Ably = Ably
window.axios = axios
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'



// Create new echo client instance using ably-js client driver.
window.Echo = new Echo({
    broadcaster: 'ably',
    key: import.meta.env.VITE_ABLY_KEY, 
});

// Register a callback for listing to connection state change 
window.Echo.connector.ably.connection.on((stateChange) => {
    console.log("LOGGER:: Connection event :: ", stateChange);
    if (stateChange.current === 'disconnected' && stateChange.reason?.code === 40142) { // key/token status expired
        console.log("LOGGER:: Connection token expired https://help.ably.io/error/40142");
    }
});

// Expose flatpickr factory with optional hijri plugin support
import flatpickr from 'flatpickr';
// hijri plugin package provides a factory function. Prefer the package's
// main entry (ESM) rather than the UMD `dist` bundle which may include
// development-only helpers (like LiveReload injection) that cause network
// errors in production environments.
// Import the UMD dist build directly (we've stripped LiveReload injection
// from the dist file in node_modules to avoid network errors in prod).
import hijriModule from 'flatpickr-hijri-calendar/dist/flatpickr-hijri-calendar.js';
// luxon DateTime is required by the hijri plugin
import { DateTime } from 'luxon';
// optional Arabic locale
import { Arabic } from 'flatpickr/dist/l10n/ar.js';
// Import flatpickr CSS locally so Vite bundles it
import 'flatpickr/dist/flatpickr.min.css';
// Import hijri plugin CSS if available
import 'flatpickr-hijri-calendar/dist/flatpickr-hijri-calendar.css';

// Attach a small factory to window.flatpickr so other modules can use it
window.flatpickr = (element, config = {}) => {
    const cfg = { ...(config || {}) };
    // If hijri flag is present, apply the hijri plugin
    if (cfg && cfg.hijri) {
        const plugins = Array.isArray(cfg.plugins) ? [...cfg.plugins] : [];
        // hijriPlugin may be a function that returns the plugin instance
        // hijriModule may be a UMD that exports the plugin factory as the default
        const hijriFactory = (hijriModule && (hijriModule.default || hijriModule.hijriCalendarPlugin || hijriModule))
            || undefined;
        if (typeof hijriFactory === 'function') {
            // pass luxon's DateTime into the plugin factory so it can perform
            // calendar conversions. The plugin signature is (dateTime, config).
            try {
                plugins.push(
                    hijriFactory(DateTime, {
                        showHijriDates: true,
                        showHijriToggle: false,
                        theme: 'light',
                    }),
                );
            } catch (e) {
                // fallback: try calling without args (older builds) — but the
                // preferred path is supplying DateTime so the plugin works.
                try {
                    plugins.push(hijriFactory());
                } catch (err) {
                    // ignore: plugin will not be added
                }
            }
        }
        cfg.plugins = plugins;

        const ensureHijriAriaLabel = (dayElem) => {
            try {
                if (!dayElem || !dayElem.dateObj) {
                    return;
                }

                const hijriLabel = DateTime
                    .fromJSDate(dayElem.dateObj)
                    .setLocale('ar-SA')
                    .reconfigure({ outputCalendar: 'islamic' })
                    .toFormat('d LLLL yyyy');

                if (hijriLabel) {
                    dayElem.setAttribute('aria-label', hijriLabel);
                }
            } catch (e) {
                // ignore formatting errors
            }
        };

        const hijriOnlyDay = (_selectedDates, _dateStr, instance, dayElem) => {
            if (!instance || !instance.calendarContainer || !dayElem) {
                return;
            }

            try {
                // Prefer computing the Hijri text via Luxon to avoid depending on
                // the plugin's internal DOM structure. Format as "day month" in
                // Arabic (e.g., "1 رمضان").
                if (dayElem.dateObj) {
                    const hv = DateTime.fromJSDate(dayElem.dateObj)
                        .setLocale('ar-SA')
                        .reconfigure({ outputCalendar: 'islamic' })
                        .toFormat('d LLLL');
                    if (hv) {
                        dayElem.innerHTML = `<span class="flatpickr-hijri-only">${hv}</span>`;
                        ensureHijriAriaLabel(dayElem);
                        return;
                    }
                }
            } catch (e) {
                // fall back to original DOM-based extraction below
            }

            // Fallback: use any plugin-rendered hijri text if available
            const wrapper = dayElem.querySelector('.flatpickr-hijri-date-wrapper');
            const hijriPart = wrapper ? wrapper.querySelector('.flatpickr-hijri-date-hijri') : null;
            const hijriValue = (hijriPart && hijriPart.textContent) || dayElem.textContent;

            if (hijriValue) {
                dayElem.innerHTML = `<span class="flatpickr-hijri-only">${hijriValue}</span>`;
            }

            ensureHijriAriaLabel(dayElem);
        };

        // Hijri month names (Arabic) — keep in sync with other code using
        // HIJRI_MONTH_NAMES so displays match across components.
        const HIJRI_MONTH_NAMES = [
            'محرم','صفر','ربيع الأول','ربيع الآخر','جمادى الأولى','جمادى الآخرة',
            'رجب','شعبان','رمضان','شوال','ذو القعدة','ذو الحجة'
        ];

        const updateGregorianControlsToHijri = (instance) => {
            if (!instance || !instance.calendarContainer) return;

            // Update month dropdown options text to Hijri names while keeping
            // the select element present so the UI remains identical.
            try {
                const monthDropdown = instance.calendarContainer.querySelector('.flatpickr-monthDropdown-months');
                if (monthDropdown && monthDropdown instanceof HTMLSelectElement) {
                    // Ensure option count matches 12; create if necessary.
                    for (let i = 0; i < 12; i++) {
                        let opt = monthDropdown.options[i];
                        if (!opt) {
                            opt = document.createElement('option');
                            opt.className = 'flatpickr-monthDropdown-month';
                            monthDropdown.appendChild(opt);
                        }
                        opt.value = String(i);
                        opt.textContent = HIJRI_MONTH_NAMES[i] || opt.textContent || String(i + 1);
                        opt.setAttribute('tabindex', '-1');
                    }
                }

                // Update the year numeric input to show the Hijri year for the
                // currently displayed month/year. Build a JS Date from the
                // instance's currentYear/currentMonth (gregorian) then convert.
                const yearInput = instance.calendarContainer.querySelector('.flatpickr-current-month .numInputWrapper input.cur-year');
                let representativeDate = null;
                if (instance.currentYear !== undefined && instance.currentMonth !== undefined) {
                    representativeDate = new Date(instance.currentYear, instance.currentMonth, 1);
                } else {
                    // Fallback: try to find any day element with a dateObj
                    const dayElem = instance.calendarContainer.querySelector('.flatpickr-day:not(.prevMonthDay):not(.nextMonthDay)');
                    if (dayElem && dayElem.dateObj) representativeDate = dayElem.dateObj;
                }

                if (representativeDate && yearInput instanceof HTMLInputElement) {
                    const hijriYear = DateTime.fromJSDate(representativeDate)
                        .setLocale('ar-SA')
                        .reconfigure({ outputCalendar: 'islamic' })
                        .toFormat('yyyy');
                    if (hijriYear) {
                        yearInput.value = hijriYear;
                    }
                }
            } catch (e) {
                // ignore any DOM errors and leave controls as-is
            }
        };

        const onDayCreateList = Array.isArray(cfg.onDayCreate) ? [...cfg.onDayCreate] : cfg.onDayCreate ? [cfg.onDayCreate] : [];
        onDayCreateList.push(hijriOnlyDay);
        cfg.onDayCreate = onDayCreateList;

        const onReadyList = Array.isArray(cfg.onReady) ? [...cfg.onReady] : cfg.onReady ? [cfg.onReady] : [];
        onReadyList.push((_s,_d,instance) => updateGregorianControlsToHijri(instance));
        cfg.onReady = onReadyList;

        const onMonthChangeList = Array.isArray(cfg.onMonthChange) ? [...cfg.onMonthChange] : cfg.onMonthChange ? [cfg.onMonthChange] : [];
        onMonthChangeList.push((_s,_d,instance) => updateGregorianControlsToHijri(instance));
        cfg.onMonthChange = onMonthChangeList;

        delete cfg.hijri;
    }

    // Ensure Arabic localization is available
    try {
        // flatpickr exposes l10ns map for locales
        flatpickr.l10ns = { ...(flatpickr.l10ns || {}), ar: Arabic };
    } catch (e) {
        // ignore
    }

    return flatpickr(element, cfg);
};

// Convenience factories to explicitly create a purely-Hijri or purely-Gregorian
// instance. This avoids relying on callers to pass a special `hijri` flag and
// makes it explicit when a calendar should be dedicated to one system.
// - `flatpickrHijri` will always apply the hijri plugin.
// - `flatpickrGregorian` will call the core flatpickr directly (no hijri plugin).
window.flatpickrHijri = (element, config = {}) => {
    const cfg = { ...(config || {}) };
    // force hijri plugin usage
    cfg.hijri = true;
    return window.flatpickr(element, cfg);
};

window.flatpickrGregorian = (element, config = {}) => {
    const cfg = { ...(config || {}) };
    // ensure hijri plugin isn't added
    if (cfg.hijri) {
        delete cfg.hijri;
    }
    // call the core flatpickr importer directly to guarantee no hijri-plugin
    // behaviour is injected by our wrapper.
    try {
        return flatpickr(element, cfg);
    } catch (e) {
        // fallback to the wrapper if something unexpected happens
        return window.flatpickr(element, cfg);
    }
};

// Provide small global helpers for showing/hiding a full-page loading spinner.
// These are used across adgroups pages and elsewhere; defining them here
// ensures they're available before page-specific bundles execute.
// They operate on an element with id `loadingSpinner` which the views include.
window.showLoading = function () {
    const spinner = document.getElementById('loadingSpinner');
    if (spinner) {
        spinner.classList.remove('d-none');
        spinner.classList.add('d-flex');
    }
    try {
        document.querySelectorAll('button').forEach(btn => btn.disabled = true);
    } catch (_) {}
};

window.hideLoading = function () {
    const spinner = document.getElementById('loadingSpinner');
    if (spinner) {
        spinner.classList.remove('d-flex');
        spinner.classList.add('d-none');
    }
    try {
        document.querySelectorAll('button').forEach(btn => btn.disabled = false);
    } catch (_) {}
};

// If any inline scripts queued flatpickr calls before the real factory loaded,
// flush the queue now by replaying queued calls against the real `flatpickr`.
(function flushFlatpickrQueue() {
    try {
        const q = window.__flatpickr_queue;
        if (Array.isArray(q) && q.length > 0 && typeof window.flatpickr === 'function') {
            for (const args of q) {
                try {
                    // args is [element, config]
                    window.flatpickr.apply(null, args);
                } catch (e) {
                    // ignore individual failures
                }
            }
        }
        // clear the queue to free memory
        if (window.__flatpickr_queue) window.__flatpickr_queue = null;
    } catch (_) {
        // ignore
    }
})();

